import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { Server, Sparkles, MessageCircle, Loader2, Route, Database, Network, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { backendStatusService } from '../../services/backendStatusService';
import { gitService } from '../../services/gitService';
import axios from 'axios';

const StatusBar = () => {
  const navigate = useNavigate();

  /**
   * Git is not a service that is up or down like the others. It is a
   * connection the user either has or does not, so it gets its own indicator
   * with its own vocabulary (connected / uncommitted / unpushed / error)
   * rather than being squeezed into the green-or-red health model.
   */
  const [gitStatus, setGitStatus] = useState({ loading: true, linked: false });

  const [serviceStatus, setServiceStatus] = useState({
    backend: { isConnected: false, loading: true },
    kafka: { isConnected: false, loading: true },
    schemaRegistry: { isConnected: false, loading: true },
    kafkaConnect: { isConnected: false, loading: true },
    spark: { isConnected: false, loading: true },
    camel: { isConnected: false, loading: true }
  });

  // Check actual service status
  useEffect(() => {
    const checkServices = async () => {
      // Check backend status
      try {
        const backendStatus = await backendStatusService.checkStatus();
        setServiceStatus(prev => ({
          ...prev,
          backend: { isConnected: backendStatus.isConnected, loading: false }
        }));
      } catch (error) {
        logger.error('Error checking backend status:', error);
        setServiceStatus(prev => ({
          ...prev,
          backend: { isConnected: false, loading: false }
        }));
      }
      
      // Check Kafka status
      try {
        // Use axios instead of fetch for better error handling
        const response = await axios.get('/api/kafka/cluster/health', {
          // Set a short timeout to prevent long waits if service is down
          timeout: 3000,
          // Only accept JSON responses
          headers: {
            'Accept': 'application/json'
          },
          // Don't throw on non-2xx responses
          validateStatus: () => true
        });
        
        if (response.status === 200) {
          setServiceStatus(prev => ({
            ...prev,
            kafka: { isConnected: true, loading: false }
          }));
        } else {
          logger.debug('Kafka service appears to be down:', response.status);
          setServiceStatus(prev => ({
            ...prev,
            kafka: { isConnected: false, loading: false }
          }));
        }
      } catch (error) {
        logger.error('Error checking Kafka status:', error);
        setServiceStatus(prev => ({
          ...prev,
          kafka: { isConnected: false, loading: false }
        }));
      }
      
      // Check Schema Registry status
      try {
        const response = await axios.get('/api/health/schema-registry', {
          timeout: 3000,
          headers: {
            'Accept': 'application/json'
          },
          validateStatus: () => true
        });

        if (response.status === 200 && response.data?.status === 'UP') {
          setServiceStatus(prev => ({
            ...prev,
            schemaRegistry: { isConnected: true, loading: false }
          }));
        } else {
          logger.debug('Schema Registry appears to be down:', response.status);
          setServiceStatus(prev => ({
            ...prev,
            schemaRegistry: { isConnected: false, loading: false }
          }));
        }
      } catch (error) {
        logger.error('Error checking Schema Registry status:', error);
        setServiceStatus(prev => ({
          ...prev,
          schemaRegistry: { isConnected: false, loading: false }
        }));
      }

      // Check Kafka Connect status
      try {
        const response = await axios.get('/api/health/kafka-connect', {
          timeout: 3000,
          headers: {
            'Accept': 'application/json'
          },
          validateStatus: () => true
        });

        if (response.status === 200 && response.data?.status === 'UP') {
          setServiceStatus(prev => ({
            ...prev,
            kafkaConnect: { isConnected: true, loading: false }
          }));
        } else {
          logger.debug('Kafka Connect appears to be down:', response.status);
          setServiceStatus(prev => ({
            ...prev,
            kafkaConnect: { isConnected: false, loading: false }
          }));
        }
      } catch (error) {
        logger.error('Error checking Kafka Connect status:', error);
        setServiceStatus(prev => ({
          ...prev,
          kafkaConnect: { isConnected: false, loading: false }
        }));
      }

      // Check Spark status using the dedicated health endpoint
      try {
        // Use axios instead of fetch for better error handling
        const response = await axios.get('/api/health/spark', {
          // Set a short timeout to prevent long waits if service is down
          timeout: 3000,
          // Only accept JSON responses
          headers: {
            'Accept': 'application/json'
          },
          // Don't throw on non-2xx responses
          validateStatus: () => true
        });

        // Check if SparkContext is active (200 status and status: "UP")
        if (response.status === 200 && response.data?.status === 'UP') {
          setServiceStatus(prev => ({
            ...prev,
            spark: { isConnected: true, loading: false }
          }));
        } else {
          logger.debug('Spark service appears to be down:', response.status, response.data?.message);
          setServiceStatus(prev => ({
            ...prev,
            spark: { isConnected: false, loading: false }
          }));
        }
      } catch (error) {
        logger.error('Error checking Spark status:', error);
        setServiceStatus(prev => ({
          ...prev,
          spark: { isConnected: false, loading: false }
        }));
      }
      
      // Check Camel status via the microservices execution engines endpoint
      try {
        const response = await axios.get('/api/microservices/execution-engines', {
          timeout: 3000,
          headers: {
            'Accept': 'application/json'
          },
          validateStatus: () => true
        });
        
        if (response.status === 200 && response.data && response.data.camel) {
          const camelEngine = response.data.camel;
          setServiceStatus(prev => ({
            ...prev,
            camel: { 
              isConnected: camelEngine.available === true, 
              loading: false,
              info: camelEngine
            }
          }));
        } else {
          logger.debug('Camel service status not available:', response.status);
          setServiceStatus(prev => ({
            ...prev,
            camel: { isConnected: false, loading: false }
          }));
        }
      } catch (error) {
        logger.error('Error checking Camel status:', error);
        setServiceStatus(prev => ({
          ...prev,
          camel: { isConnected: false, loading: false }
        }));
      }
    };
    
    const checkGit = async () => {
      try {
        const response = await gitService.listRepositories();
        const repos = response?.data || [];
        if (repos.length === 0) {
          setGitStatus({ loading: false, linked: false });
          return;
        }
        const repo = repos[0];

        // Only ask for live working-tree state once the repository is READY.
        // Reading status on a repo that has never been cloned would make the
        // backend clone it, and nothing in a navbar should be able to start a
        // minutes-long clone just because someone loaded a page.
        let working = null;
        if (repo.status === 'READY') {
          const statusResponse = await gitService.getStatus(repo.id);
          working = statusResponse?.data || null;
        }

        setGitStatus({
          loading: false,
          linked: true,
          repo,
          branch: working?.branch || repo.currentBranch,
          dirty: working ? !working.clean : false,
          ahead: working?.ahead || 0,
          error: repo.status === 'ERROR' ? repo.lastError : null,
        });
      } catch (error) {
        logger.debug('Could not read Git status for the status bar:', error);
        setGitStatus({ loading: false, linked: false });
      }
    };

    checkServices();
    checkGit();
    
    // Set up polling to check services every 30 seconds
    const gitIntervalId = setInterval(checkGit, 60000);
    const intervalId = setInterval(checkServices, 60000);
    
    // Clean up interval when component unmounts
    return () => {
      clearInterval(intervalId);
      clearInterval(gitIntervalId);
    };
  }, []);

  const ServiceIcon = ({ service, status }) => {
    const icons = {
      backend: Server,
      kafka: MessageCircle,
      schemaRegistry: Database,
      kafkaConnect: Network,
      spark: Sparkles,
      camel: Route
    };

    const serviceNames = {
      backend: 'Backend',
      kafka: 'Kafka',
      schemaRegistry: 'Schema Registry',
      kafkaConnect: 'Kafka Connect',
      spark: 'Spark',
      camel: 'Camel'
    };

    const Icon = icons[service];
    const color = status.isConnected ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400';
    const title = `${serviceNames[service]} ${status.isConnected ? 'Available' : 'Unavailable'}`;

    return (
      <div className="relative group">
        {status.loading ? (
          <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin" />
        ) : (
          <Icon className={`w-5 h-5 ${color}`} />
        )}

        {/* Standard Tooltip */}
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gray-800 dark:bg-gray-700 text-white dark:text-gray-100 text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-50">
          {title}
        </div>
      </div>
    );
  };

  const GitIcon = () => {
    let color = 'text-gray-400 dark:text-gray-500';
    let title = 'Git, not connected. Click to set it up.';

    if (gitStatus.error) {
      color = 'text-red-500 dark:text-red-400';
      title = `Git, ${gitStatus.error}`;
    } else if (gitStatus.linked) {
      const parts = [`Git, ${gitStatus.repo?.name}`, `on ${gitStatus.branch}`];
      if (gitStatus.dirty) {
        color = 'text-amber-500 dark:text-amber-400';
        parts.push('uncommitted changes');
      } else if (gitStatus.ahead > 0) {
        color = 'text-amber-500 dark:text-amber-400';
        parts.push(`${gitStatus.ahead} commit${gitStatus.ahead === 1 ? '' : 's'} to push`);
      } else {
        color = 'text-green-500 dark:text-green-400';
        parts.push('up to date');
      }
      title = parts.join(' · ');
    }

    return (
      <button
        type="button"
        onClick={() => navigate('/settings?tab=git')}
        className="relative group flex items-center focus:outline-none"
        aria-label={title}
      >
        {gitStatus.loading ? (
          <Loader2 className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-spin" />
        ) : (
          <GitBranch className={`w-5 h-5 ${color} transition-colors`} />
        )}

        {/* A dot rather than a count: the exact number lives in the designer's
            status bar, this only needs to say "something is waiting". */}
        {!gitStatus.loading && (gitStatus.dirty || gitStatus.ahead > 0) && (
          <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-2 ring-white dark:ring-gray-900" />
        )}

        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-gray-800 dark:bg-gray-700 text-white dark:text-gray-100 text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-50">
          {title}
        </div>
      </button>
    );
  };

  return (
    <div className="flex items-center space-x-4">
      <ServiceIcon service="backend" status={serviceStatus.backend} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <ServiceIcon service="kafka" status={serviceStatus.kafka} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <ServiceIcon service="schemaRegistry" status={serviceStatus.schemaRegistry} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <ServiceIcon service="kafkaConnect" status={serviceStatus.kafkaConnect} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <ServiceIcon service="spark" status={serviceStatus.spark} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <ServiceIcon service="camel" status={serviceStatus.camel} />
      <div className="h-4 w-px bg-gray-200 dark:bg-gray-600"></div>
      <GitIcon />
    </div>
  );
};

export default StatusBar; 